import { initDB } from '@/lib/db';

const CURRENT_SCHEMA_VERSION = 2;

/**
 * Check and migrate the IndexedDB schema on app load.
 * Handles corrupted data gracefully with fallbacks.
 */
export async function checkAndMigrateSchema(): Promise<void> {
  try {
    const db = await initDB();

    // Verify all expected object stores exist
    const requiredStores = ['starter', 'feedingHistory', 'schedules', 'wizard', 'settings'] as const;
    const missingStores = requiredStores.filter(
      (store) => !db.objectStoreNames.contains(store),
    );

    if (missingStores.length > 0) {
      console.warn(
        `[migration] Missing object stores: ${missingStores.join(', ')}. ` +
          'The database may need to be recreated.',
      );
    }

    // Validate existing data integrity
    await validateDataIntegrity(db);

    console.info(`[migration] Schema v${CURRENT_SCHEMA_VERSION} verified successfully.`);
  } catch (error) {
    console.error('[migration] Schema check failed:', error);
    // Don't throw — the app should still work with defaults
  }
}

async function validateDataIntegrity(
  db: Awaited<ReturnType<typeof initDB>>,
): Promise<void> {
  try {
    // Check starter data
    const starters = await db.getAll('starter');
    for (const starter of starters) {
      if (!starter.id || !starter.name) {
        console.warn('[migration] Found starter with missing required fields, skipping.');
      }
    }

    // Check feeding history
    const feedings = await db.getAll('feedingHistory');
    for (const feeding of feedings) {
      if (!feeding.id || !feeding.date) {
        console.warn('[migration] Found feeding entry with missing required fields.');
      }
    }
  } catch (error) {
    console.warn('[migration] Data integrity check encountered an error:', error);
    // Non-fatal: app can still operate with partial data
  }
}

/**
 * Get the current schema version.
 */
export function getSchemaVersion(): number {
  return CURRENT_SCHEMA_VERSION;
}
